/**
 * Sign-up leads: the mobile number someone gave on the first sign-up screen, kept even when they
 * stop before a company exists, so the sales team can call them.
 *
 * WHY THIS EXISTS. Sign-up asks for the number first, then the SMS code, the team and the company,
 * and nothing reached the server until the very last step. Between 30 Oct and 4 Nov 2026 about 140
 * app users reached that screen and no number was kept.
 *
 * WHAT IT RECORDS (implemented in the follow-up PR, stated here so the contract has one meaning):
 *   - One row per number. The page posts when the person presses Continue, with no proof, and again
 *     when the SMS code passes, with `phoneProof`. Together they show where people stopped.
 *   - `verifiedAt` is set ONLY by a proof the server checked. A number without one was typed by
 *     someone, not proven to be theirs, and the panel must say so.
 *   - A proof is read here, never spent: `register` needs the same token at the end of the flow.
 *   - A bad proof is not an error. The number is kept unverified and the caller gets the same 200,
 *     so this route cannot be used to test tokens or to learn whether a number is known.
 *   - It must never hold sign-up up: the page does not wait for it and ignores any failure.
 *
 * `termsAccepted` is required because screen 1 will not continue without it. That is the consent
 * for keeping the number, and the screen must say the team may call.
 *
 * Deliberately a separate contract, as `notification-email-mute.contract.ts` is: the `auth` and
 * `platform` routers are typed exhaustively against their contracts, so adding routes to them would
 * force handlers into this PR and break "contract in its own PR first" (CLAUDE.md, invariant 8).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema, phoneSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** Where the sign-up ran. Ad installs land on `android`. */
export const SIGNUP_LEAD_SOURCES = ['web', 'android'] as const;
export const signupLeadSourceSchema = z.enum(SIGNUP_LEAD_SOURCES);
export type SignupLeadSource = z.infer<typeof signupLeadSourceSchema>;

export const recordSignupLeadBodySchema = z.object({
  /** E.164, the number on the form. */
  phone: phoneSchema,
  /** The Firebase ID token once the SMS code has passed, as at `register`. Absent before that. */
  phoneProof: z.string().min(20).max(4096).optional(),
  /** Screen 1 cannot continue without this, so a request without it did not come from the page. */
  termsAccepted: z.literal(true),
  source: signupLeadSourceSchema,
});
export type RecordSignupLeadBody = z.infer<typeof recordSignupLeadBodySchema>;

/** The same answer whatever happened, so it says nothing about the number or the proof. */
export const recordSignupLeadResponseSchema = z.object({ recorded: z.literal(true) });
export type RecordSignupLeadResponse = z.infer<typeof recordSignupLeadResponseSchema>;

/**
 * Where a lead stands, derived rather than stored:
 *   - `started`   — gave a number, never passed the SMS code
 *   - `verified`  — passed the SMS code, has no company
 *   - `converted` — a company's owner holds this number
 */
export const SIGNUP_LEAD_STAGES = ['started', 'verified', 'converted'] as const;
export const signupLeadStageSchema = z.enum(SIGNUP_LEAD_STAGES);
export type SignupLeadStage = z.infer<typeof signupLeadStageSchema>;

export const signupLeadRowSchema = z.object({
  id: uuidSchema,
  phone: phoneSchema,
  source: signupLeadSourceSchema,
  stage: signupLeadStageSchema,
  firstSeenAt: instantSchema,
  lastSeenAt: instantSchema,
  /** When the SMS code passed. Null means the number is unproven: it may not be theirs. */
  verifiedAt: instantSchema.nullable(),
  /** The company this number went on to own, when `stage` is `converted`. */
  companyName: z.string().nullable(),
});
export type SignupLeadRow = z.infer<typeof signupLeadRowSchema>;

export const signupLeadsQuerySchema = z.object({
  stage: signupLeadStageSchema.optional(),
  /** Only leads first seen within this many days. */
  seenWithinDays: z.coerce.number().int().min(1).max(3650).optional(),
  /** Matches digits anywhere in the number. */
  q: z.string().trim().min(1).max(32).optional(),
});
export type SignupLeadsQuery = z.infer<typeof signupLeadsQuerySchema>;

/** Counts across ALL leads, so the funnel does not move with the list's filters. */
export const signupLeadStatsSchema = z.object({
  started: z.number().int().nonnegative(),
  verified: z.number().int().nonnegative(),
  converted: z.number().int().nonnegative(),
});
export type SignupLeadStats = z.infer<typeof signupLeadStatsSchema>;

export const signupLeadsResponseSchema = z.object({
  items: z.array(signupLeadRowSchema),
  stats: signupLeadStatsSchema,
});
export type SignupLeadsResponse = z.infer<typeof signupLeadsResponseSchema>;

export const signupLeadContract = c.router({
  recordSignupLead: {
    method: 'POST',
    path: '/auth/signup/lead',
    body: recordSignupLeadBodySchema,
    responses: {
      200: recordSignupLeadResponseSchema,
      400: errorBodySchema,
      422: errorBodySchema,
      429: errorBodySchema,
    },
    summary:
      'Keep the mobile number from the first sign-up screen so the team can call. Answers the ' +
      'same whatever the number or proof, and is never spent: `register` still uses the proof.',
    metadata: routeMeta('public', { ai: 'hidden' }),
  },

  listSignupLeads: {
    method: 'GET',
    path: '/platform/signup-leads',
    query: signupLeadsQuerySchema,
    responses: {
      200: signupLeadsResponseSchema,
      400: errorBodySchema,
      401: errorBodySchema,
      403: errorBodySchema,
      422: errorBodySchema,
      500: errorBodySchema,
    },
    summary: 'Numbers given at sign-up, newest first, with how far each one got',
    // Same as every /platform route: `null` here, the platform session is checked by its router.
    metadata: routeMeta(null, { ai: 'hidden' }),
  },
});
